import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useGamification } from '../../contexts/GamificationContext';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import QRScanner from '../../components/QRScanner';
import CandidateRating from '../../components/CandidateRating';
import CandidateEvaluations from '../../components/CandidateEvaluations';
import PointsDisplay from '../../components/gamification/PointsDisplay';
import BadgesDisplay from '../../components/gamification/BadgesDisplay';
import ActivityFeed from '../../components/gamification/ActivityFeed';
import Leaderboard from '../../components/gamification/Leaderboard';
import * as FiIcons from 'react-icons/fi';

const { FiBriefcase, FiUsers, FiCalendar, FiClock, FiCheck, FiX, FiEye, FiUser, FiQrCode, FiStar } = FiIcons;

const CompanyDashboard = () => {
  const { jobs, interviewRequests, interviews, updateInterviewRequest } = useData();
  const { user } = useAuth();
  const { addPoints, awardBadge } = useGamification();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [interviewDateTime, setInterviewDateTime] = useState('');
  const [boothLocation, setBoothLocation] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [activeTab, setActiveTab] = useState('requests');

  // Filter data for this company
  const companyJobs = jobs.filter(job => job.companyId === user.id);
  const companyRequests = interviewRequests.filter(req => req.companyId === user.id);
  const companyInterviews = interviews.filter(int => int.companyId === user.id);

  const handleAcceptRequest = () => {
    if (!selectedRequest || !interviewDateTime || !boothLocation) return;
    
    updateInterviewRequest(selectedRequest.id, {
      status: 'accepted',
      interviewTime: interviewDateTime,
      boothLocation: boothLocation,
      companyId: user.id,
      candidateId: selectedRequest.candidateId
    });
    
    // Award points for scheduling interview
    addPoints('interviewScheduled');
    
    // Check for badges
    const totalScheduled = companyInterviews.length + 1;
    if (totalScheduled >= 30) {
      awardBadge('fairChampion');
    }
    
    setSelectedRequest(null);
    setInterviewDateTime('');
    setBoothLocation('');
  };

  const handleDeclineRequest = (requestId) => {
    updateInterviewRequest(requestId, { status: 'declined' });
  };

  const handleScanSuccess = (decodedText) => {
    // Parse the scanned QR code data
    // In a real app, this would fetch candidate data from the decoded URL
    const mockCandidate = {
      id: '123',
      name: 'John Doe',
      email: 'john.doe@example.com',
      phone: '+1 (555) 123-4567',
      field: 'Software Engineering',
      experience: '3-5 years (Mid Level)',
      skills: 'React, Node.js, Python, TypeScript',
      education: "Bachelor's in Computer Science from XYZ University, 2020",
      city: 'New York, USA',
      companyId: user.id
    };
    
    setSelectedCandidate(mockCandidate);
    setShowScanner(false);
    setShowRating(true);
  };

  const handleRatingSubmit = async (evaluationData) => {
    // In a real app, this would save the evaluation to the database
    console.log('Evaluation submitted:', evaluationData);
    
    // Award points for rating candidate
    addPoints('candidateRated');
    
    // Check for talent scout badge
    const totalCandidates = 51; // Mock data - in real app, track actual count
    if (totalCandidates >= 50) {
      awardBadge('talentScout');
    }
    
    setShowRating(false);
    setSelectedCandidate(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'accepted': return 'text-green-600 bg-green-100';
      case 'declined': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (!user.profileComplete) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <SafeIcon icon={FiBriefcase} className="text-blue-600 text-4xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-blue-900 mb-2">Complete Your Company Profile</h2>
          <p className="text-blue-700 mb-4">
            Please complete your company profile to start receiving interview requests.
            <span className="font-semibold"> Earn 100 points!</span>
          </p>
          <Link to="/company/profile" className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2">
            <SafeIcon icon={FiBriefcase} />
            <span>Complete Profile</span>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Dashboard</h1>
        <p className="text-gray-600">Manage your job postings, interview requests, and candidate evaluations.</p>
      </motion.div>

      {/* Gamification Section */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 space-y-6">
          <PointsDisplay />
          <ActivityFeed />
        </div>
        <div className="space-y-6">
          <BadgesDisplay />
        </div>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <SafeIcon icon={FiBriefcase} className="text-blue-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Job Openings</p>
              <p className="text-2xl font-bold text-gray-900">{companyJobs.length}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-yellow-100 p-3 rounded-lg">
              <SafeIcon icon={FiClock} className="text-yellow-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Requests</p>
              <p className="text-2xl font-bold text-gray-900">
                {companyRequests.filter(req => req.status === 'pending').length}
              </p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <SafeIcon icon={FiCalendar} className="text-green-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Scheduled Interviews</p>
              <p className="text-2xl font-bold text-gray-900">{companyInterviews.length}</p>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg">
              <SafeIcon icon={FiStar} className="text-purple-600 text-xl" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Evaluations</p>
              <p className="text-2xl font-bold text-gray-900">12</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* QR Scanner Button */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-8">
        <button
          onClick={() => setShowScanner(true)}
          className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center space-x-2 shadow-lg"
        >
          <SafeIcon icon={FiQrCode} />
          <span>Scan Candidate QR Code</span>
          <span className="text-xs bg-white/20 px-2 py-1 rounded-full">+20 pts</span>
        </button>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('requests')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Interview Requests
            </button>
            <button
              onClick={() => setActiveTab('interviews')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'interviews'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Scheduled Interviews
            </button>
            <button
              onClick={() => setActiveTab('evaluations')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'evaluations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Candidate Evaluations
            </button>
            <button
              onClick={() => setActiveTab('leaderboard')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'leaderboard'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Leaderboard
            </button>
          </nav>
        </div>
      </motion.div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'requests' && (
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Interview Requests</h2>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {companyRequests.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No interview requests yet.</p>
              ) : (
                companyRequests.map((request) => (
                  <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{request.candidateName}</h3>
                        <p className="text-sm text-gray-600">
                          {request.type === 'specific' ? `Position: ${request.jobTitle}` : 'General Interview'}
                        </p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                          {request.status === 'accepted' && (
                            <span className="text-xs text-green-600 font-medium">+25 pts</span>
                          )}
                        </div>
                        {request.status === 'accepted' && request.interviewTime && (
                          <div className="mt-2 text-sm text-gray-600">
                            <p>📅 {new Date(request.interviewTime).toLocaleString()}</p>
                            <p>📍 Booth {request.boothLocation}</p>
                          </div>
                        )}
                      </div>
                      {request.status === 'pending' && (
                        <div className="flex space-x-2 ml-4">
                          <button onClick={() => setSelectedRequest(request)} className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors">
                            Accept
                          </button>
                          <button onClick={() => handleDeclineRequest(request.id)} className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors">
                            Decline
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'interviews' && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Scheduled Interviews</h2>
            </div>
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {companyInterviews.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No scheduled interviews yet.</p>
              ) : (
                companyInterviews.map((interview) => (
                  <div key={interview.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{interview.candidateName}</h3>
                        <p className="text-sm text-gray-600">
                          {interview.type === 'specific' ? `Position: ${interview.jobTitle}` : 'General Interview'}
                        </p>
                        <div className="mt-2 text-sm text-gray-600 space-y-1">
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiCalendar} />
                            <span>{new Date(interview.interviewTime).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <SafeIcon icon={FiUsers} />
                            <span>Booth {interview.boothLocation}</span>
                          </div>
                        </div>
                      </div>
                      <button className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors">
                        <SafeIcon icon={FiEye} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'evaluations' && (
          <CandidateEvaluations />
        )}

        {activeTab === 'leaderboard' && (
          <Leaderboard userRole="company" />
        )}
      </div>

      {/* Accept Interview Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule Interview with {selectedRequest.candidateName}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Interview Date & Time</label>
                <input
                  type="datetime-local"
                  value={interviewDateTime}
                  onChange={(e) => setInterviewDateTime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Booth Location</label>
                <input
                  type="text"
                  value={boothLocation}
                  onChange={(e) => setBoothLocation(e.target.value)}
                  placeholder="e.g., A1, B2, C3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">
                  <strong>Request Type:</strong> {selectedRequest.type === 'specific' ? `${selectedRequest.jobTitle}` : 'General Interview'}
                </p>
                <p className="text-xs text-blue-600 mt-1">+25 points for scheduling!</p>
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleAcceptRequest}
                disabled={!interviewDateTime || !boothLocation}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Schedule Interview
              </button>
              <button
                onClick={() => setSelectedRequest(null)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner
          onScanSuccess={handleScanSuccess}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Candidate Rating Modal */}
      {showRating && selectedCandidate && (
        <CandidateRating
          candidate={selectedCandidate}
          onClose={() => setShowRating(false)}
          onSubmit={handleRatingSubmit}
        />
      )}
    </div>
  );
};

export default CompanyDashboard;